import { motion } from "framer-motion";
import { useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Checkbox } from "./ui/Checkbox";
import { LogoFull } from "./Logo";
import { emailVariants } from "../data/emailVariants";
import { profiles } from "../data/profiles";
import type { Profile } from "../data/questions";
import { getOrCreateEmailVariant } from "../lib/storage";
import { isValidEmail } from "../lib/utils";
import { submitLead } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import type { QuizContext } from "../lib/utm";

interface EmailCaptureScreenProps {
  profile: Profile;
  percentage: number;
  answers: number[];
  context: QuizContext;
  onDone: () => void;
}

export function EmailCaptureScreen({
  profile,
  percentage,
  answers,
  context,
  onDone,
}: EmailCaptureScreenProps) {
  const variantKey = useMemo(() => getOrCreateEmailVariant(), []);
  const variant = emailVariants[variantKey];
  const profileData = profiles[profile];

  const [email, setEmail] = useState("");
  const [optOut, setOptOut] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error("Email invalide", { description: "Vérifie ton adresse." });
      return;
    }
    setLoading(true);
    trackEvent("email_submit", { variant: variantKey, opt_out: optOut });
    const res = await submitLead({
      email: email.trim(),
      profile,
      percentage,
      optOut,
      variant: variantKey,
      answers,
      context,
    });
    setLoading(false);
    if (!res.success) {
      // UX > tracking : on continue quand même.
      toast.error("Petit souci d'envoi…", {
        description: "Pas de panique, on te montre quand même ton résultat.",
      });
    } else {
      toast.success("Reçu ! 🎉", { description: "On t'a envoyé ton profil par email." });
    }
    onDone();
  };

  return (
    <section className="relative min-h-[100svh] bg-background flex flex-col">
      <div className="max-w-md mx-auto w-full px-6 py-8 flex-1 flex flex-col">
        <div className="flex justify-center mb-8 opacity-80">
          <LogoFull />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center mb-6"
        >
          <span className="px-4 py-2 rounded-full bg-vert-eau/30 text-vert-empire font-heading font-bold text-sm">
            Tu as un profil {profileData.name}… 👀
          </span>
        </motion.div>

        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", delay: 0.15 }}
          className="text-center text-6xl mb-2 animate-bounce"
          aria-hidden
        >
          🚀
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="font-heading font-black text-3xl text-center text-foreground"
        >
          {variant.title}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-2 text-center font-body text-muted-foreground"
        >
          {variant.subtitle}
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          onSubmit={submit}
          className="mt-8 flex flex-col gap-4"
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="ton@email.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Adresse email"
          />

          <Checkbox
            id="opt-out"
            checked={optOut}
            onCheckedChange={setOptOut}
          >
            Je ne souhaite pas recevoir les informations liées aux prochains projets d'Ommistack
          </Checkbox>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full rounded-2xl"
            loading={loading}
          >
            {variant.cta}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {variant.microcopy}
          </p>
        </motion.form>
      </div>
    </section>
  );
}
